import { Injectable, inject } from '@angular/core';
import {
  ExternalAppConnectionStatusResponse,
  ExternalAppRequestConnectionResponse,
} from '@cropx-web/screens/partner-connection/user-account/models/AccountDetails';
import {
  WiseconnOverviewFarm,
  WiseconnOverviewResponse,
} from '@cropx-web/screens/partner-connection/user-account/wiseconn/wiseconn-overview/model/WiseconnOverviewResponse';
import { FieldResponse } from '@cropx-web/services/api-gateway/CompaniesApiService';
import { ApiService } from '@cropx-web/services/network/ApiService';
import {
  Action,
  State,
  StateContext,
  createPropertySelectors,
} from '@ngxs/store';
import { EMPTY, Observable, catchError } from 'rxjs';
import { tap } from 'rxjs/operators';

const WISECONN_APP_ID = 9;

export enum InternalEntityType {
  IRRIGATION_ZONE = 'IRRIGATION_ZONE',
  FIELD = 'FIELD',
}

export enum WiseconnEntityType {
  PUMP_SYSTEM = 'PUMP_SYSTEM',
  ZONE = 'ZONE',
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Wiseconn {
  const type = 'Wiseconn';

  export class ConnectToExternalService {
    static readonly type = `[${type}] ConnectToExternalService`;

    constructor(public apiKey: string) {}
  }

  export class CheckWiseconnConnection {
    static readonly type = `[${type}] CheckWiseconnConnection`;

    constructor(public userId: number) {}
  }

  export class GetWiseconnOverview {
    static readonly type = `[${type}] GetWiseconnOverview`;

    constructor() {}
  }

  export class ConnectWiseconnEntity {
    static readonly type = `[${type}] ConnectWiseconnEntity`;

    constructor(public body: any) {}
  }

  export class DisconnectWiseconnEntity {
    static readonly type = `[${type}] DisconnectWiseconnEntity`;

    constructor(public body: any) {}
  }

  export class DisconnectExternalApp {
    static readonly type = `[${type}] DisconnectWiseconnApp`;

    constructor(public userId: string) {}
  }
}

export interface WiseconnStateModel {
  isConnected: boolean | undefined;
  isLoading: boolean;
  overviewLoader: boolean;
  overviewData: WiseconnOverviewFarm[];
  formloading: boolean;
}

@State<WiseconnStateModel>({
  name: 'wiseconn',
  defaults: {
    isConnected: undefined,
    isLoading: false,
    overviewLoader: false,
    overviewData: [],
    formloading: false,
  },
})
@Injectable()
export class WiseconnState {
  private apiService = inject(ApiService);

  @Action(Wiseconn.ConnectToExternalService)
  connectToWiseconnService(
    ctx: StateContext<WiseconnStateModel>,
    action: Wiseconn.ConnectToExternalService,
  ): Observable<FieldResponse> {
    ctx.patchState({ isLoading: true });
    return this.apiService.userApi.connectToWiseconApp(action.apiKey).pipe(
      tap(() => {
        ctx.patchState({ isConnected: true, isLoading: false });
      }),
      catchError((err) => {
        ctx.patchState({ isConnected: false, isLoading: false });
        return err;
      }),
    );
  }

  @Action(Wiseconn.CheckWiseconnConnection)
  checkWiseconnConnection(
    ctx: StateContext<WiseconnStateModel>,
    action: Wiseconn.CheckWiseconnConnection,
  ): Observable<ExternalAppConnectionStatusResponse> {
    ctx.patchState({ isLoading: true });
    return this.apiService.userApi
      .getExternalAppConnectionStatus(action.userId, WISECONN_APP_ID)
      .pipe(
        tap((response: ExternalAppConnectionStatusResponse) => {
          ctx.patchState({
            isConnected: response?.content?.isConnected,
            isLoading: false,
          });
        }),
        catchError(() => {
          ctx.patchState({ isConnected: false, isLoading: false });
          return EMPTY;
        }),
      );
  }

  @Action(Wiseconn.GetWiseconnOverview)
  getWiseconnOverview(ctx: StateContext<WiseconnStateModel>): Observable<any> {
    ctx.patchState({ overviewLoader: true });
    return this.apiService.userApi.getWiseconnOverview().pipe(
      tap((response: WiseconnOverviewResponse) => {
        const farms = response.content.farms;
        farms.forEach((farm) => {
          farm.pumpSystems.forEach((pump) => {
            pump.irrigationZones.forEach(
              (zone) => (zone.pumpId = pump.wiseconnId),
            );
            pump.totalZones = new Set(
              pump.irrigationZones
                .filter((zone) => zone.irrigationZone)
                .map((zone) => zone.irrigationZone.id),
            ).size;
            pump.totalFields = new Set(
              pump.irrigationZones
                .filter((zone) => zone.field)
                .map((zone) => zone.field.id),
            ).size;
            pump.isConnected = pump.totalFields > 0 || pump.totalZones > 0;
          });
        });
        ctx.patchState({
          overviewLoader: false,
          overviewData: farms,
        });
      }),
      catchError(() => {
        ctx.patchState({ overviewLoader: false });
        return EMPTY;
      }),
    );
  }

  @Action(Wiseconn.ConnectWiseconnEntity)
  connectWiseconnEntity(
    ctx: StateContext<WiseconnStateModel>,
    action: Wiseconn.ConnectWiseconnEntity,
  ): Observable<any> {
    ctx.patchState({ formloading: true });
    const body = action.body;
    return this.apiService.userApi.connectWiseconnEntity(body).pipe(
      tap(() => {
        ctx.patchState({ formloading: false });
      }),
      catchError(() => {
        ctx.patchState({ formloading: false });
        return EMPTY;
      }),
    );
  }

  @Action(Wiseconn.DisconnectWiseconnEntity)
  disconnectWiseconnEntity(
    _ctx: StateContext<WiseconnStateModel>,
    action: Wiseconn.DisconnectWiseconnEntity,
  ): Observable<any> {
    return this.apiService.userApi
      .disconnectWiseconnEntity(action.body)
      .pipe(tap(() => {}));
  }

  @Action(Wiseconn.DisconnectExternalApp)
  disconnectWiseconnApp(
    ctx: StateContext<WiseconnStateModel>,
    action: Wiseconn.DisconnectExternalApp,
  ): Observable<any> {
    return this.apiService.userApi
      .disconnectExternalAppObservable(WISECONN_APP_ID, action.userId)
      .pipe(
        tap((response: ExternalAppRequestConnectionResponse) => {
          ctx.patchState({
            isConnected: response.content.isConnect,
            isLoading: false,
          });
        }),
        catchError(() => {
          ctx.patchState({ isLoading: false });
          return EMPTY;
        }),
      );
  }
}

export class WiseconnStateSelectors {
  static slices = createPropertySelectors<WiseconnStateModel>(WiseconnState);
}
